import { useCallback, useEffect, useRef, useState } from 'react';

// `autoCloseMs` null keeps the card until the user acts. Hovering or focusing the card pauses
// the countdown; it resumes with the time that was left.
export const useToastAutoClose = (autoCloseMs: number | null, onClose: () => void) => {
  const [isPaused, setIsPaused] = useState(false);
  const remainingMsRef = useRef(autoCloseMs);
  // The stack passes a new callback on every render; that must not restart the countdown.
  const onCloseRef = useRef(onClose);

  onCloseRef.current = onClose;

  const pause = useCallback(() => setIsPaused(true), []);

  const resume = useCallback(() => setIsPaused(false), []);

  useEffect(() => {
    if (!autoCloseMs || isPaused) {
      return;
    }

    const startedAt = Date.now();
    const delayMs = remainingMsRef.current ?? autoCloseMs;
    const timer = setTimeout(() => onCloseRef.current(), delayMs);

    return () => {
      clearTimeout(timer);
      remainingMsRef.current = Math.max(0, delayMs - (Date.now() - startedAt));
    };
  }, [autoCloseMs, isPaused]);

  return { pause, resume };
};
